import { useState } from "react";
import type { FormEvent } from "react";
import { LoanKind } from "@xynex-money/shared";
import { ApiError } from "../api";
import { useCrudResource } from "../hooks/useCrudResource";
import { Table } from "../components/Table";
import type { Column } from "../components/Table";
import { Modal } from "../components/Modal";
import { MoneyInput } from "../components/MoneyInput";
import { DateInput } from "../components/DateInput";
import { fmtDate, fmtINR } from "../fmt";

const LOAN_KINDS = LoanKind.options;

interface Loan {
  id: string;
  lender: string;
  kind: LoanKind;
  loanNumber: string | null;
  principal: number | null;
  rate: number | null;
  emi: number | null;
  startDate: string | null;
  tenureMonths: number | null;
  outstanding: number | null;
  notes: string | null;
}

interface FormState {
  lender: string;
  kind: LoanKind;
  loanNumber: string;
  principal: number | null;
  rate: number | null;
  emi: number | null;
  startDate: string;
  tenureMonths: number | null;
  outstanding: number | null;
  notes: string;
}

const emptyForm: FormState = {
  lender: "",
  kind: "bank",
  loanNumber: "",
  principal: null,
  rate: null,
  emi: null,
  startDate: "",
  tenureMonths: null,
  outstanding: null,
  notes: "",
};

export function Loans() {
  const { items: loans, loading, error, setError, create, update, remove } = useCrudResource<Loan>("/api/loans");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Loan | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setModalOpen(true);
  };
  const openEdit = (l: Loan) => {
    setEditing(l);
    setForm({
      lender: l.lender,
      kind: l.kind,
      loanNumber: l.loanNumber ?? "",
      principal: l.principal,
      rate: l.rate,
      emi: l.emi,
      startDate: l.startDate ?? "",
      tenureMonths: l.tenureMonths,
      outstanding: l.outstanding,
      notes: l.notes ?? "",
    });
    setFormError(null);
    setModalOpen(true);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const body = {
      lender: form.lender,
      kind: form.kind,
      loanNumber: form.loanNumber || null,
      principal: form.principal,
      rate: form.rate,
      emi: form.emi,
      startDate: form.startDate || null,
      tenureMonths: form.tenureMonths,
      outstanding: form.outstanding,
      notes: form.notes || null,
    };
    try {
      if (editing) await update(editing.id, body);
      else await create(body);
      setModalOpen(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "save failed");
    }
  };

  const onDelete = async (l: Loan) => {
    if (!confirm(`Delete loan "${l.lender}"?`)) return;
    try {
      await remove(l.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "delete failed");
    }
  };

  const columns: Column<Loan>[] = [
    { key: "lender", header: "Lender" },
    { key: "kind", header: "Kind" },
    { key: "loanNumber", header: "Loan No.", render: (l) => l.loanNumber ?? "" },
    { key: "principal", header: "Principal", align: "right", render: (l) => fmtINR(l.principal) },
    { key: "emi", header: "EMI", align: "right", render: (l) => fmtINR(l.emi) },
    { key: "outstanding", header: "Outstanding", align: "right", render: (l) => fmtINR(l.outstanding) },
    { key: "startDate", header: "Start", render: (l) => fmtDate(l.startDate) },
    {
      key: "actions",
      header: "",
      render: (l) => (
        <span className="actions-inline">
          <button type="button" onClick={() => openEdit(l)}>
            Edit
          </button>
          <button type="button" className="danger" onClick={() => onDelete(l)}>
            Delete
          </button>
        </span>
      ),
    },
  ];

  return (
    <div>
      <h1>Loans</h1>
      {error && <div className="error-text">{error}</div>}
      <div className="toolbar">
        <button className="primary" type="button" onClick={openAdd}>
          Add loan
        </button>
      </div>
      <Table columns={columns} rows={loans} rowKey={(l) => l.id} emptyText={loading ? "Loading…" : "No loans yet."} />

      {modalOpen && (
        <Modal title={editing ? "Edit loan" : "Add loan"} onClose={() => setModalOpen(false)}>
          <form onSubmit={onSubmit} className="modal-body">
            <div className="form-grid">
              <div className="field">
                <label htmlFor="l-lender">Lender</label>
                <input id="l-lender" required value={form.lender} onChange={(e) => setForm({ ...form, lender: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="l-kind">Kind</label>
                <select id="l-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as FormState["kind"] })}>
                  {LOAN_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="l-loan-number">Loan No.</label>
                <input
                  id="l-loan-number"
                  value={form.loanNumber}
                  onChange={(e) => setForm({ ...form, loanNumber: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="l-principal">Principal</label>
                <MoneyInput value={form.principal} onChange={(v) => setForm({ ...form, principal: v })} />
              </div>
              <div className="field">
                <label htmlFor="l-rate">Rate (%)</label>
                <MoneyInput value={form.rate} onChange={(v) => setForm({ ...form, rate: v })} />
              </div>
              <div className="field">
                <label htmlFor="l-emi">EMI</label>
                <MoneyInput value={form.emi} onChange={(v) => setForm({ ...form, emi: v })} />
              </div>
              <div className="field">
                <label htmlFor="l-start">Start date</label>
                <DateInput value={form.startDate} onChange={(v) => setForm({ ...form, startDate: v })} />
              </div>
              <div className="field">
                <label htmlFor="l-tenure">Tenure (months)</label>
                <input
                  id="l-tenure"
                  type="number"
                  value={form.tenureMonths ?? ""}
                  onChange={(e) => setForm({ ...form, tenureMonths: e.target.value === "" ? null : Number(e.target.value) })}
                />
              </div>
              <div className="field">
                <label htmlFor="l-outstanding">Outstanding</label>
                <MoneyInput value={form.outstanding} onChange={(v) => setForm({ ...form, outstanding: v })} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="l-notes">Notes</label>
              <textarea id="l-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {formError && <div className="error-text">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button className="primary" type="submit">
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
